import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { useL10n } from '@uplift/hooks';
import {
  EMPTY_STRING,
} from '@uplift/constants';
import { Button, TextInput, UpliftThemeProvider } from '@uplift/ui-v2';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useForgotPasswordValidationSchema } from './hooks';

import { PurpleAlert } from '../../../login-page/components/login-header.component';
import { useForgotPasswordMutation } from '@uplift/queries';


const useStyles = makeStyles()((theme) => ({
  box: {
    position: 'relative',
    maxWidth: '510px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  form: {
    width: '100%',
    maxWidth: '660px',
  },
  text: {
    fontSize: '24px',
    lineHeight: '133.4%',
    maxWidth: '510px',
    textAlign: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 0,
    marginBottom: theme.spacing(4),
  },
  supportEmail: {
    textDecoration: 'underline',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  alert: {
    marginTop: theme.spacing(5),
  },
  button: {
    width: '100%',
    maxWidth: '432px',
    marginTop: theme.spacing(6),
    marginLeft: 0,
    marginRight: 0,
  },
}));

export type ForgotPasswordFormType = {
  email: string;
};

export const ForgotPasswordForm: React.FC = () => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const validationSchema = useForgotPasswordValidationSchema(l10n);
  const forgotPasswordMutation = useForgotPasswordMutation();
  const [ isShowInfo, setIsShowInfo ] = useState(false);
  const [ isSubmitDisabled, setIsSubmitDisabled ] = useState(false);

  const initialValues: ForgotPasswordFormType = {
    email: EMPTY_STRING,
  };

  const onSubmit = async ({ email }: ForgotPasswordFormType) => {
    setIsSubmitDisabled(true);
    try {
      await forgotPasswordMutation.mutateAsync({
        email,
      });

      setIsShowInfo(true);
    } catch (error) {
      console.error(error);
      setIsSubmitDisabled(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <p className={classes.text}>{l10n.auth.forgotText}</p>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form
            onSubmit={formik.handleSubmit}
            className={classes.form}
          >
            <Box className={classes.box}>
              <TextInput
                id="forgot-password"
                name="email"
                type="email"
                label={l10n.auth.email}
                fullWidth
              />
            </Box>
            <Box textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="legacyPrimary"
                size="large"
                disabled={isSubmitDisabled}
                className={classes.button}
                loading={formik.isSubmitting}
              >
                {l10n.auth.passwordReset}
              </Button>
            </Box>

            {isShowInfo && (
              <Box className={classes.alert}>
                <UpliftThemeProvider>
                  <PurpleAlert title={l10n.auth.forgotPasswordInfo.ifWeFoundYourAccount}>
                    {l10n.auth.forgotPasswordInfo.dontSeeAnEmail}
                    <a
                      href={'mailto:' + l10n.auth.forgotPasswordInfo.supportEmail}
                      className={classes.supportEmail}
                    >
                      {l10n.auth.forgotPasswordInfo.supportEmail}
                    </a>
                  </PurpleAlert>
                </UpliftThemeProvider>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};
